import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { UserProfile } from '@mindweb/shared'

const BASE_URL = import.meta.env.VITE_API_URL ?? ''

interface AuthState {
  readonly accessToken: string | null
  readonly refreshToken: string | null
  readonly user: UserProfile | null
  readonly isAuthenticated: boolean
  readonly isLoginDialogOpen: boolean

  setTokens: (accessToken: string, refreshToken: string) => void
  setUser: (user: UserProfile) => void
  refreshTokens: () => Promise<boolean>
  logout: () => void
  openLoginDialog: () => void
  closeLoginDialog: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoginDialogOpen: false,

      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken, isAuthenticated: true })
      },

      setUser: (user) => {
        set({ user })
      },

      refreshTokens: async () => {
        const { refreshToken } = get()
        if (!refreshToken) return false

        try {
          const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          })

          if (response.status === 401 || response.status === 403) {
            get().logout()
            return false
          }

          if (!response.ok) {
            return false
          }

          const data = await response.json() as {
            success: boolean
            data?: { accessToken: string; refreshToken?: string }
          }
          if (data.success && data.data) {
            set({
              accessToken: data.data.accessToken,
              ...(data.data.refreshToken ? { refreshToken: data.data.refreshToken } : {}),
            })
            return true
          }

          get().logout()
          return false
        } catch {
          return false
        }
      },

      logout: () => {
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        })
      },

      openLoginDialog: () => {
        set({ isLoginDialogOpen: true })
      },

      closeLoginDialog: () => {
        set({ isLoginDialogOpen: false })
      },
    }),
    {
      name: 'kg-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
